import { useBldgStore } from "./../store/bldgStore";
import { LoginPersonDTO } from "./../store/generatedFetchClient";
// metrics.js
// generic wrapper for Metrics provider
// which is currently Posthog.com

import { PostHog } from "posthog-js";
import posthog from "posthog-js";
import { PortalDTO } from "../store/generatedFetchClient";
import { fillMissingDates } from "../helpers";

let metrics: PostHog | null = null;

//TODO: MOVE OUT OF REPO!
const writeOnlyKey = "phc_WvmJiGHIvMrUMjdOjmPpf0JQlTWv2RdNPYyPH54Zq4k";
const portalReadOnlyKey = "phx_13TI7LoMFkFCTJaxj4uMpLdgyEShVzyBpHLM6XtY5NlQzn4o";
const projectId = 110120;

export function useMetrics() {
  function install(app: any) {
    app.config.globalProperties.$posthog = metrics = 
      posthog.init?.(writeOnlyKey, {
        api_host: 'https://m.akpreparedness.com',
        defaults: '2026-05-30',
        enable_recording_console_log: true,
    });

    console.log("Metrics started. Sending events to Posthog.com");
  }

  /// Set or overwrite group identites with this user session
  /// Can be called before user authenticatoin
  function setGroups(portal: PortalDTO | null) {
    if (portal && metrics) {
      metrics.group("portal", portal.portalId!.toString(), {
        name: portal.name,
        siteCode: portal.siteCode,
        isRespondPortal: portal.isRespondEnabled,
        isPreparePortal: portal.isTrainingEnabled,
        isVRPortal: portal.isVrtourEnabled,
        isAlertPortal: portal.isAlertEnabled,
        isUsingSSO: portal.isSSOEnabled,
        serverVersion: portal.serverVersion,
        clientVersion: import.meta.env.VITE_SAFETYNET_VERSION,
        versionMismatch: portal.serverVersion != import.meta.env.VITE_SAFETYNET_VERSION,
      });

      metrics.group("client", portal.clientId!.toString(), {
        category: portal.category,
        client: portal.clientId,
        clientName: portal.clientName, //region
      });
    }
  }

  // Associate anon user with authenticated user at login time
  function identify(user?: LoginPersonDTO | undefined) {
    if (user && metrics) {
      let siteRole = useBldgStore().lookupSiteRoleName(user.siteRoleId);

      metrics.identify(
        user.loginEmail, // use email as unique id
        {
          email: user.loginEmail,
          name: user.firstName + " " + user.lastName,
          personId: user.personId,
          portalId: user.portalId,
          tenantName: user.tenantSystemName,
          tenantId: user.tenantId,
          siteRoleId: user.siteRoleId,
          siteRoleAlias: siteRole?.roleAlias,
          siteRoleName: siteRole?.systemName,
          isStaff: user.isStaffRole,

          // optional: set additional person properties
        }
      );
    }
  }

  function pageView() {
    // Capture current url as a page view
    metrics?.capture(MetricCaptureNames.PageView);
  }

  function pageLeave() {
    metrics?.capture(MetricCaptureNames.PageLeave);
  }

  function capture(eventName: string, params?: {}) {
    metrics?.capture(eventName, params);
  }

  function register() {
    //metrics.register
  }

  function reset() {
    metrics?.reset();
  }

  return { install, identify, pageView, pageLeave, capture, register, setGroups, reset };
}

export async function getPortalMetricGraphData(portalId: number): Promise<FilteredGraphs> {
  let responses = await Promise.all([
    runQuery(
      `select toDate(timestamp) as timestamp, count(DISTINCT \`$session_id\`) as sessionCount from events where $group_0 = '${portalId}' and timestamp <= now() group by timestamp order by timestamp desc limit 100`
    ),
    runQuery(
      `select toDate(timestamp) as timestamp, count(DISTINCT person_id) as uniqueUserCount from events where $group_0 = '${portalId}' and timestamp <= now() group by timestamp order by timestamp desc limit 100`
    ),
    runQuery(
      `select toDate(timestamp) as timestamp, count() as uniqueUserCount from events where $group_0 = '${portalId}' and event = '${MetricCaptureNames.PageView}' and timestamp <= now() group by timestamp order by timestamp desc limit 100`
    ),
    runQuery(
      `select toDate(timestamp) as timestamp, count() as uniqueUserCount from events where $group_0 = '${portalId}' and event = '${MetricCaptureNames.RageClick}' and timestamp <= now() group by timestamp order by timestamp desc limit 100`
    ),
    runQuery(
      `select toDate(timestamp) as timestamp, count() as uniqueUserCount from events where $group_0 = '${portalId}' and event = '${MetricCaptureNames.ViewRespondContent}' and timestamp <= now() group by timestamp order by timestamp desc limit 100`
    ),
    runQuery(
      `select toDate(timestamp) as timestamp, count() as uniqueUserCount from events where $group_0 = '${portalId}' and event = '${MetricCaptureNames.VideoEnded}' and timestamp <= now() group by timestamp order by timestamp desc limit 100`
    ),
    runQuery(
      `select toDate(timestamp) as timestamp, count() as uniqueUserCount from events where $group_0 = '${portalId}' and event = '${MetricCaptureNames.QuestionAnswered}' and timestamp <= now() group by timestamp order by timestamp desc limit 100`
    ),
    runQuery(
      `select toDate(timestamp) as timestamp, count() as uniqueUserCount from events where $group_0 = '${portalId}' and event = '${MetricCaptureNames.TestCompleted}' and timestamp <= now() group by timestamp order by timestamp desc limit 100`
    ),
  ]);

  const reversedJsons = await Promise.all(responses.map((r) => r.json()));

  let sessionsData: GraphData = {
    dates: reversedJsons[0].results.reverse().map((row: any[]) => row[0]),
    dataSeries: [reversedJsons[0].results.reverse().map((row: any[]) => row[1])],
  };

  let uniqueUsersData: GraphData = {
    dates: reversedJsons[1].results.reverse().map((row: any[]) => row[0]),
    dataSeries: [reversedJsons[1].results.reverse().map((row: any[]) => row[1])],
  };

  let pageViewsData: GraphData = {
    dates: reversedJsons[2].results.reverse().map((row: any[]) => row[0]),
    dataSeries: [reversedJsons[2].results.reverse().map((row: any[]) => row[1])],
  };

  let rageClicksData: GraphData = {
    dates: reversedJsons[3].results.reverse().map((row: any[]) => row[0]),
    dataSeries: [reversedJsons[3].results.reverse().map((row: any[]) => row[1])],
  };

  let respondData: GraphData = {
    dates: reversedJsons[4].results.reverse().map((row: any[]) => row[0]),
    dataSeries: [reversedJsons[4].results.reverse().map((row: any[]) => row[1])],
  };

  let videosData: GraphData = {
    dates: reversedJsons[5].results.reverse().map((row: any[]) => row[0]),
    dataSeries: [reversedJsons[5].results.reverse().map((row: any[]) => row[1])],
  };

  let questionsData: GraphData = {
    dates: reversedJsons[6].results.reverse().map((row: any[]) => row[0]),
    dataSeries: [reversedJsons[6].results.reverse().map((row: any[]) => row[1])],
  };

  let testsData: GraphData = {
    dates: reversedJsons[7].results.reverse().map((row: any[]) => row[0]),
    dataSeries: [reversedJsons[7].results.reverse().map((row: any[]) => row[1])],
  };

  return {
    sessions: fillMissingDates(sessionsData),
    uniqueUsers: fillMissingDates(uniqueUsersData),
    pageViews: fillMissingDates(pageViewsData),
    rageClicks: fillMissingDates(rageClicksData),
    respondViews: fillMissingDates(respondData),
    videosEnded: fillMissingDates(videosData),
    questionsAnswered: fillMissingDates(questionsData),
    testsCompleted: fillMissingDates(testsData),
  } as FilteredGraphs;
}

export async function getClientMetricGraphData(clientId: number): Promise<FilteredGraphs> {
  let responses = await Promise.all([
    runQuery(
      `select toDate(timestamp) as timestamp, count(DISTINCT \`$session_id\`) as sessionCount from events where $group_1 = '${clientId}' and timestamp <= now() group by timestamp order by timestamp desc limit 100`
    ),
    runQuery(
      `select toDate(timestamp) as timestamp, count(DISTINCT person_id) as uniqueUserCount from events where $group_1 = '${clientId}' and timestamp <= now() group by timestamp order by timestamp desc limit 100`
    ),
    runQuery(
      `select toDate(timestamp) as timestamp, count() as uniqueUserCount from events where $group_1 = '${clientId}' and event = '${MetricCaptureNames.PageView}' and timestamp <= now() group by timestamp order by timestamp desc limit 100`
    ),
    runQuery(
      `select toDate(timestamp) as timestamp, count() as uniqueUserCount from events where $group_1 = '${clientId}' and event = '${MetricCaptureNames.RageClick}' and timestamp <= now() group by timestamp order by timestamp desc limit 100`
    ),
    runQuery(
      `select toDate(timestamp) as timestamp, count() as uniqueUserCount from events where $group_1 = '${clientId}' and event = '${MetricCaptureNames.ViewRespondContent}' and timestamp <= now() group by timestamp order by timestamp desc limit 100`
    ),
    runQuery(
      `select toDate(timestamp) as timestamp, count() as uniqueUserCount from events where $group_1 = '${clientId}' and event = '${MetricCaptureNames.VideoEnded}' and timestamp <= now() group by timestamp order by timestamp desc limit 100`
    ),
    runQuery(
      `select toDate(timestamp) as timestamp, count() as uniqueUserCount from events where $group_1 = '${clientId}' and event = '${MetricCaptureNames.QuestionAnswered}' and timestamp <= now() group by timestamp order by timestamp desc limit 100`
    ),
    runQuery(
      `select toDate(timestamp) as timestamp, count() as uniqueUserCount from events where $group_1 = '${clientId}' and event = '${MetricCaptureNames.TestCompleted}' and timestamp <= now() group by timestamp order by timestamp desc limit 100`
    ),
  ]);

  const reversedJsons = await Promise.all(responses.map((r) => r.json()));

  let sessionsData: GraphData = {
    dates: reversedJsons[0].results.reverse().map((row: any[]) => row[0]),
    dataSeries: [reversedJsons[0].results.reverse().map((row: any[]) => row[1])],
  };

  let uniqueUsersData: GraphData = {
    dates: reversedJsons[1].results.reverse().map((row: any[]) => row[0]),
    dataSeries: [reversedJsons[1].results.reverse().map((row: any[]) => row[1])],
  };

  let pageViewsData: GraphData = {
    dates: reversedJsons[2].results.reverse().map((row: any[]) => row[0]),
    dataSeries: [reversedJsons[2].results.reverse().map((row: any[]) => row[1])],
  };

  let rageClicksData: GraphData = {
    dates: reversedJsons[3].results.reverse().map((row: any[]) => row[0]),
    dataSeries: [reversedJsons[3].results.reverse().map((row: any[]) => row[1])],
  };

  let respondData: GraphData = {
    dates: reversedJsons[4].results.reverse().map((row: any[]) => row[0]),
    dataSeries: [reversedJsons[4].results.reverse().map((row: any[]) => row[1])],
  };

  let videosData: GraphData = {
    dates: reversedJsons[5].results.reverse().map((row: any[]) => row[0]),
    dataSeries: [reversedJsons[5].results.reverse().map((row: any[]) => row[1])],
  };

  let questionsData: GraphData = {
    dates: reversedJsons[6].results.reverse().map((row: any[]) => row[0]),
    dataSeries: [reversedJsons[6].results.reverse().map((row: any[]) => row[1])],
  };

  let testsData: GraphData = {
    dates: reversedJsons[7].results.reverse().map((row: any[]) => row[0]),
    dataSeries: [reversedJsons[7].results.reverse().map((row: any[]) => row[1])],
  };

  return {
    sessions: fillMissingDates(sessionsData),
    uniqueUsers: fillMissingDates(uniqueUsersData),
    pageViews: fillMissingDates(pageViewsData),
    rageClicks: fillMissingDates(rageClicksData),
    respondViews: fillMissingDates(respondData),
    videosEnded: fillMissingDates(videosData),
    questionsAnswered: fillMissingDates(questionsData),
    testsCompleted: fillMissingDates(testsData),
  } as FilteredGraphs;
}

export async function getPortalMetrics(
  portalId: number,
  fromDate: Date,
  toDate: Date
): Promise<MetricsReturnData> {
  let results: MetricsReturnData;

  try {
    const responses = await Promise.all([
      runQuery(
        `SELECT DISTINCT distinct_id FROM events WHERE $group_0 = '${portalId}' AND distinct_id LIKE '%@%' AND timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' )`
      ),
      runQuery(
        `SELECT 
          (SELECT COUNT(*) FROM (SELECT DISTINCT person_id FROM events WHERE $group_0 = '${portalId}' AND timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ))) as userCount, 
          (SELECT COUNT(*) FROM (SELECT event FROM events WHERE $group_0 = '${portalId}' AND timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ) AND event = '${
          MetricCaptureNames.PageView
        }')) as pageViews,
          (SELECT COUNT(*) FROM (SELECT DISTINCT $session_id FROM events WHERE $group_0 = '${portalId}' AND timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ))) as sessions,
          (SELECT COUNT(*) FROM (SELECT event FROM events WHERE $group_0 = '${portalId}' AND timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ) AND event = '${
          MetricCaptureNames.RageClick
        }')) as rageClicks,
          (SELECT COUNT(*) FROM (SELECT event FROM events WHERE $group_0 = '${portalId}' AND timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ) AND event = '${
          MetricCaptureNames.VideoEnded
        }')) as videosFinished,
          (SELECT COUNT(*) FROM (SELECT event FROM events WHERE $group_0 = '${portalId}' AND timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ) AND event = '${
          MetricCaptureNames.ViewRespondContent
        }')) as respondViews,
          (SELECT COUNT(*) FROM (SELECT event FROM events WHERE $group_0 = '${portalId}' AND timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ) AND event = '${
          MetricCaptureNames.QuestionAnswered
        }')) as questionsAnswered,
          (SELECT COUNT(*) FROM (SELECT event FROM events WHERE $group_0 = '${portalId}' AND timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ) AND event = '${
          MetricCaptureNames.TestCompleted
        }')) as testsCompleted,
        `
      ),
    ]);

    const jsons = await Promise.all(responses.map((r) => r.json()));

    results = {
      uniqueUsers: jsons[1].results[0][0],
      pageViews: jsons[1].results[0][1],
      sessions: jsons[1].results[0][2],
      rageClicks: jsons[1].results[0][3],
      videosEnded: jsons[1].results[0][4],
      respondViews: jsons[1].results[0][5],
      questionsAnswered: jsons[1].results[0][6],
      testsCompleted: jsons[1].results[0][7],
      loggedInUsers: jsons[0].results.map((p: string[]) => p[0]),
      errorString: "",
    };
  } catch (e) {
    console.log(e);
    results = {
      uniqueUsers: 0,
      pageViews: 0,
      sessions: 0,
      loggedInUsers: [],
      errorString: (e as Error).toString(),
    };
  }

  console.log("results", results);

  return results;
}

export async function getClientMetrics(
  clientId: number,
  fromDate: Date,
  toDate: Date
): Promise<MetricsReturnData> {
  let results: MetricsReturnData;

  try {
    const responses = await Promise.all([
      runQuery(
        `SELECT DISTINCT distinct_id FROM events WHERE $group_1 = '${clientId}' AND distinct_id LIKE '%@%' AND timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' )`
      ),
      runQuery(
        `SELECT 
          (SELECT COUNT(*) FROM (SELECT DISTINCT person_id FROM events WHERE $group_1 = '${clientId}' AND timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ))) as userCount, 
          (SELECT COUNT(*) FROM (SELECT event FROM events WHERE $group_1 = '${clientId}' AND timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ) AND event = '${
          MetricCaptureNames.PageView
        }')) as pageViews,
          (SELECT COUNT(*) FROM (SELECT DISTINCT $session_id FROM events WHERE $group_1 = '${clientId}' AND timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ))) as sessions,
          (SELECT COUNT(*) FROM (SELECT event FROM events WHERE $group_1 = '${clientId}' AND timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ) AND event = '${
          MetricCaptureNames.RageClick
        }')) as rageClicks,
          (SELECT COUNT(*) FROM (SELECT event FROM events WHERE $group_1 = '${clientId}' AND timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ) AND event = '${
          MetricCaptureNames.VideoEnded
        }')) as videosFinished,
          (SELECT COUNT(*) FROM (SELECT event FROM events WHERE $group_1 = '${clientId}' AND timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ) AND event = '${
          MetricCaptureNames.ViewRespondContent
        }')) as respondViews,
          (SELECT COUNT(*) FROM (SELECT event FROM events WHERE $group_1 = '${clientId}' AND timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ) AND event = '${
          MetricCaptureNames.QuestionAnswered
        }')) as questionsAnswered,
          (SELECT COUNT(*) FROM (SELECT event FROM events WHERE $group_1 = '${clientId}' AND timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ) AND event = '${
          MetricCaptureNames.TestCompleted
        }')) as testsCompleted,
        `
      ),
    ]);

    const jsons = await Promise.all(responses.map((r) => r.json()));

    results = {
      uniqueUsers: jsons[1].results[0][0],
      pageViews: jsons[1].results[0][1],
      sessions: jsons[1].results[0][2],
      rageClicks: jsons[1].results[0][3],
      videosEnded: jsons[1].results[0][4],
      respondViews: jsons[1].results[0][5],
      questionsAnswered: jsons[1].results[0][6],
      testsCompleted: jsons[1].results[0][7],
      loggedInUsers: jsons[0].results.map((p: string[]) => p[0]),
      errorString: "",
    };
  } catch (e) {
    console.log(e);
    results = {
      uniqueUsers: 0,
      pageViews: 0,
      sessions: 0,
      loggedInUsers: [],
      errorString: (e as Error).toString(),
    };
  }

  console.log("results", results);

  return results;
}

export async function getGlobalMetrics(fromDate: Date, toDate: Date): Promise<MetricsReturnData> {
  let results: MetricsReturnData;

  try {
    const responses = await Promise.all([
      runQuery(
        `SELECT 
          (SELECT COUNT(*) FROM (SELECT DISTINCT person_id FROM events WHERE timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ))) as userCount, 
          (SELECT COUNT(*) FROM (SELECT event FROM events WHERE timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ) AND event = '${
          MetricCaptureNames.PageView
        }')) as pageViews,
          (SELECT COUNT(*) FROM (SELECT DISTINCT $session_id FROM events WHERE timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ))) as sessions,
          (SELECT COUNT(*) FROM (SELECT event FROM events WHERE timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ) AND event = '${
          MetricCaptureNames.RageClick
        }')) as rageClicks,
          (SELECT COUNT(*) FROM (SELECT event FROM events WHERE timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ) AND event = '${
          MetricCaptureNames.VideoEnded
        }')) as videosFinished,
          (SELECT COUNT(*) FROM (SELECT event FROM events WHERE timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ) AND event = '${
          MetricCaptureNames.ViewRespondContent
        }')) as respondViews,
          (SELECT COUNT(*) FROM (SELECT event FROM events WHERE timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ) AND event = '${
          MetricCaptureNames.QuestionAnswered
        }')) as questionsAnswered,
          (SELECT COUNT(*) FROM (SELECT event FROM events WHERE timestamp > toDateTime( '${fromDate.toISOString()}' ) AND timestamp < toDateTime( '${toDate.toISOString()}' ) AND event = '${
          MetricCaptureNames.TestCompleted
        }')) as testsCompleted,
        `
      ),
    ]);

    const jsons = await Promise.all(responses.map((r) => r.json()));

    console.log(jsons);

    results = {
      uniqueUsers: jsons[0].results[0][0],
      pageViews: jsons[0].results[0][1],
      sessions: jsons[0].results[0][2],
      rageClicks: jsons[0].results[0][3],
      videosEnded: jsons[0].results[0][4],
      respondViews: jsons[0].results[0][5],
      questionsAnswered: jsons[0].results[0][6],
      testsCompleted: jsons[0].results[0][7],
      errorString: "",
    };
  } catch (e) {
    console.log(e);
    results = {
      uniqueUsers: 0,
      pageViews: 0,
      sessions: 0,
      loggedInUsers: [],
      errorString: (e as Error).toString(),
    };
  }

  console.log("results", results);

  return results;
}

async function runQuery(query: string): Promise<Response> {
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${portalReadOnlyKey}`,
  };

  const payload = {
    query: {
      kind: "HogQLQuery",
      query: query,
    },
  };

  return fetch(`https://us.i.posthog.com/api/projects/${projectId}/query/`, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
}

export type MetricsReturnData = {
  uniqueUsers: number;
  pageViews: number;
  sessions: number;
  rageClicks?: number;
  videosEnded?: number;
  respondViews?: number;
  questionsAnswered?: number;
  testsCompleted?: number;
  loggedInUsers?: string[];
  errorString: string;
};

export type FilteredGraphs = {
  sessions: GraphData;
  uniqueUsers: GraphData;
  pageViews: GraphData;
  rageClicks: GraphData;
  videosEnded: GraphData;
  respondViews: GraphData;
  questionsAnswered: GraphData;
  testsCompleted: GraphData;
};

export type GraphData = {
  dates: Date[];
  dataSeries: number[][];
};

export enum MetricCaptureNames {
  PageView = "$pageview",
  PageLeave = "$pageleave",
  RageClick = "$rageclick",
  ViewRespondContent = "viewRespondContent",
  VideoStarted = "videoStarted",
  VideoEnded = "videoEnded",
  QuestionAnswered = "questionAnswered",
  TestCompleted = "testCompleted",
}
